import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChartBar, ClockCounterClockwise, CreditCard, GearSix, MagnifyingGlass, Scales, SignOut, UsersThree } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth-context";
import Logo from "@/components/Logo";
import PoweredByBadge from "@/components/PoweredByBadge";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: ChartBar, testId: "nav-dashboard" },
  { to: "/app/new", label: "New Audit", icon: MagnifyingGlass, testId: "nav-new-audit" },
  { to: "/app/history", label: "History", icon: ClockCounterClockwise, testId: "nav-history" },
  { to: "/app/compare", label: "Compare", icon: Scales, testId: "nav-compare" },
  { to: "/app/workspace", label: "Workspace", icon: UsersThree, testId: "nav-workspace" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testId: "nav-billing" },
];

export default function AppShell({ children }) {
  const { user, billing, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const planBadge = billing && (
    <span className={`mono text-[10px] tracking-wider px-2 py-1 rounded-sm border font-semibold ${
      billing.plan === "free" ? "border-ink-muted text-ink-muted bg-secondary" :
      billing.plan === "trial" ? "border-warning text-warning bg-warning/5" :
      "border-success text-success bg-success/5"
    }`} data-testid="sidebar-plan-badge">
      {billing.plan_label?.toUpperCase()}
    </span>
  );

  return (
    <div className="app-dark min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-[hsl(var(--surface))] sticky top-0 h-screen">
        <Link
          to="/app/dashboard"
          className="px-6 py-5 border-b border-border block"
          data-testid="sidebar-logo"
        >
          <div className="label-overline mb-2">Site Auditor</div>
          <Logo variant="dark" size="h-9" testId="sidebar-logo-img" />
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testId }) => {
            const active = loc.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-150 border-l-2 ${
                  active
                    ? "border-brand bg-secondary text-ink font-semibold"
                    : "border-transparent text-ink-muted hover:text-ink hover:bg-secondary"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                <span className="text-sm">{label}</span>
              </Link>
            );
          })}
          {user?.is_admin && (
            <>
              <Link
                to="/app/admin"
                data-testid="nav-admin"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-150 border-l-2 ${
                  loc.pathname === "/app/admin"
                    ? "border-brand bg-secondary text-ink font-semibold"
                    : "border-transparent text-ink-muted hover:text-ink hover:bg-secondary"
                }`}
              >
                <GearSix size={18} />
                <span className="text-sm">Admin · Settings</span>
              </Link>
              <Link
                to="/app/admin/users"
                data-testid="nav-admin-users"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-150 border-l-2 ${
                  loc.pathname.startsWith("/app/admin/users")
                    ? "border-brand bg-secondary text-ink font-semibold"
                    : "border-transparent text-ink-muted hover:text-ink hover:bg-secondary"
                }`}
              >
                <UsersThree size={18} />
                <span className="text-sm">Admin · Users</span>
              </Link>
            </>
          )}
        </nav>
        <div className="px-3 py-4 border-t border-border">
          <div className="px-3 mb-3">
            <PoweredByBadge variant="dark" />
          </div>
          <div className="px-3 py-2 mb-2 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="label-overline">Signed in</div>
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
                {user?.name}
              </div>
              <div className="text-xs text-ink-muted truncate">{user?.email}</div>
            </div>
            {planBadge}
          </div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink-muted hover:text-critical hover:bg-secondary rounded-sm transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="lg:hidden border-b border-border bg-[hsl(var(--surface))] px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <Link to="/app/dashboard" className="inline-flex items-center" data-testid="mobile-logo">
            <Logo variant="dark" size="h-7" />
          </Link>
          <div className="flex items-center gap-2">
            {NAV.map(({ to, icon: Icon, testId }) => {
              const active = loc.pathname.startsWith(to);
              return (
                <Link key={to} to={to} data-testid={`${testId}-mobile`}
                  className={`p-2 rounded-sm ${active ? "bg-brand text-white" : "text-ink-muted"}`}>
                  <Icon size={18} />
                </Link>
              );
            })}
            <button onClick={() => { logout(); navigate("/"); }}
              data-testid="logout-button-mobile" className="p-2 text-ink-muted">
              <SignOut size={18} />
            </button>
          </div>
        </div>
        <div className="px-6 py-8 lg:px-10 lg:py-10 max-w-[1400px] mx-auto">
          {children}
        </div>
        <div className="lg:hidden border-t border-border bg-[hsl(var(--surface))] px-4 py-3 flex items-center justify-center">
          <PoweredByBadge variant="dark" />
        </div>
      </main>
    </div>
  );
}
